'use strict'

import React, { useRef, useEffect, useCallback, useMemo } from 'react'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import { MAPPING_TYPE_GROUPS, PREVIEW_EMPTY_CELL_LABEL } from '../extra/constants'
import { SimpleMappingForm } from './SimpleMappingForm'
import { ConstantForm } from './ConstantForm'
import { getMappingPreview } from '../extra/services'
import { DateFormatForm } from './DateFormatForm'
import { TimeFormatForm } from './TimeFormatForm'
import { ConversionTableForm } from './ConversionTableForm'
import { ValueMappingInputForm } from './ValueMappingInputForm'
import { ValueMappingDndForm, VALUE_MAPPING_DND_UNSELECTED_COLUMN } from './ValueMappingDndForm'
import { FourArithmeticForm } from './FourArithmeticForm'
import { SingleConditionForm } from './SingleConditionForm'
import { MultiConditionForm } from './MultiConditionForm'
import {
  MappingModalProvider,
  useMappingModalState,
  useMappingModalDispatch,
  VALIDATION_HANDLERS,
  MAPPING_INFO_CONVERTERS
} from '../extra/mappingModalContext'
import {
  _mappingInfoToStoreItem,
  buildIf02099MappingPreviewBody,
  isNumericColumn,
  mappingPreviewApiResponseToRows,
  normalizeIf02099PreviewErrors,
  normalizeIf02099PreviewWarnings,
  resolveMappingPreviewApiErrorMessage,
  getValidationIssueCellKeySet,
  getValidationIssueColumnLevelSet,
  isPositiveRowNumber,
  mapIf02099PreviewIssuesToValidationIssues,
  formatIf02099PreviewIssueHead
} from '../extra/helpers'
import { ChrofyCustomSelectContext } from '../../../common/ChrofyCustomSelect'
import { ChrofyShowErrors } from '../../../common/Form'
import { getMessage } from '../../../../common/utils/MessageUtils'
import { ErrorIcon, WarningIcon, ValidationMappingLegend, getValidationMappingCellTitle } from './ValidationStatusIcons'

const MAPPING_MODAL_SELECT_CONTEXT = { dropdownWidth: 'content', dropdownMaxWidth: 300 }

/** フッターの「プレビュー表示」ボタン名。フッター更新時のボタン特定に使用する。 */
const PREVIEW_BUTTON_NAME = 'プレビュー表示'

/** プレビューを実行できないマッピング種別。固定値(constant)はプレビュー API を呼ばないため 「プレビュー表示」ボタンを非活性にする。 */
const PREVIEW_DISABLED_TYPES = ['constant']

/**
 * プレビューセルの値を描画する。 空値（null / undefined / 空文字）はセルが潰れないよう、データ検証結果テーブルと同じ 「(未入力)
 * 空値」プレースホルダー（グレー斜体）で表示する。エラー・警告該当セルではアイコンを用いる。
 *
 * @param {unknown} value セル値
 * @param {{ showErrorIcon?: boolean; showWarningIcon?: boolean }} [options]
 * @returns {React.ReactElement}
 */
const renderPreviewCell = (value, { showErrorIcon = false, showWarningIcon = false } = {}) => {
  const isEmpty = value === undefined || value === null || value === ''
  const valueEl = isEmpty ? (
    <span
      className={`whitespace-nowrap ${
        showErrorIcon ? 'italic text-red-700' : showWarningIcon ? 'italic text-amber-800' : 'italic text-[#94a3b8]'
      }`}
    >
      {PREVIEW_EMPTY_CELL_LABEL}
    </span>
  ) : (
    String(value)
  )
  return (
    <div className={`flex items-center gap-1.5 px-0.5 py-0.5 ${isEmpty ? '' : 'min-w-0'}`}>
      {showErrorIcon && <ErrorIcon />}
      {!showErrorIcon && showWarningIcon && <WarningIcon />}
      <span className={isEmpty ? 'shrink-0' : 'min-w-0 truncate'}>{valueEl}</span>
    </div>
  )
}

/** 種別ごとの説明 */
const MAPPING_TYPE_DESCRIPTIONS = {
  simple: {
    title: 'そのまま取り込む',
    desc: 'ファイルの値をそのままシステムに取り込みます。変換や加工は行いません。',
    example: '例：ファイルの「氏名」列 → システムの「氏名」項目にそのまま登録'
  },
  constant: {
    title: '固定値を設定する',
    desc: '全データに対して、指定した固定値を設定します。ファイルの値は使用しません。',
    example: '例：全従業員の「雇用区分」を「正社員」で一括登録'
  },
  value_mapping: {
    title: '個別に値を変換する（手入力）',
    desc: '特定の値を別の値に1対1で変換します。',
    example: '例：「M」→「男性」、「F」→「女性」に変換'
  },
  value_mapping_dnd: {
    title: '個別に値を変換する（D&D）',
    desc: 'ファイル項目の値をドラッグアンドドロップでシステム項目の値に紐づけます。',
    example: '例：ファイルの「M」をドラッグしてシステムの「男性」にドロップ'
  },
  conversion_table: {
    title: '変換表マッピング',
    desc: '変換表を使って値を変換します。',
    example: '例：登録済みの変換表でコード→名称に変換'
  },
  date_format: {
    title: '日付形式を変換する',
    desc: 'ファイルの日付形式をシステムの形式（YYYY-MM-DD）に変換します。',
    example: '例：「2024/11/01」や「20241101」→「2024-11-01」に変換'
  },
  time_format: {
    title: '時間形式を変換する',
    desc: '時間の表記を10進数の時間に変換します。',
    example: '例：「2:30」（2時間30分）→「2.5」（時間）に変換'
  },
  four_arithmetic: {
    title: '計算して求める（四則演算）',
    desc: '複数の項目を使って四則演算を行い、計算結果を登録します。',
    example: '例：「基本給」＋「手当」＝「支給総額」を計算'
  },
  single_condition: {
    title: '条件によって値を変える（単一項目）',
    desc: '特定の条件に一致する場合に、指定した値を設定します。',
    example: '例：「雇用区分」が「1」なら「正社員」、「2」なら「契約社員」を設定'
  },
  multi_condition: {
    title: '複数の条件を組み合わせる',
    desc: '複数の条件をAND/ORで組み合わせて、より複雑な分岐を設定します。',
    example: '例：「部門」が「営業」かつ「役職」が「課長以上」なら「管理職手当あり」を設定'
  }
}

/** マッピング設定モーダルの本体。Context から state を購読し、単一プレビュー表示。 */
export const MappingConfigModalContent = ({
  urlParams,
  globalStore,
  fieldKey,
  colName,
  initialMappingInfo,
  applyRef,
  previewRef,
  fileTypes,
  meta,
  onSubmitMappingItem
}) => {
  const state = useMappingModalState()
  const dispatch = useMappingModalDispatch()
  const { updateButtons } = useModalExtra()
  const conversionTableList = meta?.conversionTableList ?? []
  const colMeta = (meta?.genericChrofyColumns ?? []).find(
    (c) => `${c.genericTaskCode}_${c.chrofyColumnCode}` === fieldKey
  )
  const systemValues = colMeta?.valueMappingColumns ?? []
  const isNumeric = isNumericColumn(colMeta)

  useEffect(() => {
    dispatch({ type: 'OPEN', payload: { fieldKey, colName, fileTypes, initialMappingInfo } })
  }, [])

  useEffect(() => {
    applyRef.current = () => {
      const { selectedType, formData } = state
      // 種別未選択のまま「設定を適用」した場合、無言で閉じずにエラーを表示して保存を阻止する
      if (!selectedType) {
        dispatch({
          type: 'SET_ERRORS',
          payload: { errors: { selectedType: [getMessage('E010017', 'マッピング種別')] } }
        })
        return true
      }
      const validator = VALIDATION_HANDLERS[selectedType]
      if (validator) {
        const errs = validator(formData, fileTypes)
        if (errs && Object.keys(errs).length > 0) {
          dispatch({ type: 'SET_ERRORS', payload: { errors: errs } })
          return true
        }
      }
      const converter = MAPPING_INFO_CONVERTERS[selectedType]
      if (!converter) return true
      const mappingInfo = converter(formData)
      const item = _mappingInfoToStoreItem(mappingInfo)
      onSubmitMappingItem?.({ fieldKey, item })
      dispatch({ type: 'RESET' })
      return false
    }
  })

  // プレビュー取得はフッターの「プレビュー表示」ボタン押下時のみ実行する（state 変更では呼ばない）
  const runPreview = useCallback(async () => {
    const { selectedType, formData } = state
    const converter = MAPPING_INFO_CONVERTERS[selectedType]
    if (!converter) return
    const mappingInfo = converter(formData)

    // constant はプレビュー不要（ボタン非活性と同じ判定を使う）
    if (PREVIEW_DISABLED_TYPES.includes(selectedType)) return

    // ファイル列参照が必要な種別（mapping_file_type / mapping_column_name が mapping_info 内で必須）
    const COLUMN_REQUIRED_TYPES = [
      'simple',
      'value_mapping',
      'value_mapping_dnd',
      'date_format',
      'time_format',
      'conversion_table'
    ]

    if (COLUMN_REQUIRED_TYPES.includes(selectedType)) {
      const mappingFileType = (formData.mappingFileType ?? '').toString()
      const rawMappingColumnName = formData.mappingColumnName
      const isValueMappingDnd = selectedType === 'value_mapping_dnd'
      const hasSelectedColumn = isValueMappingDnd
        ? rawMappingColumnName !== null &&
          rawMappingColumnName !== undefined &&
          rawMappingColumnName !== VALUE_MAPPING_DND_UNSELECTED_COLUMN
        : Boolean(rawMappingColumnName)
      const selectedFile = fileTypes.find((f) => f.mappingFileType === mappingFileType)
      if (!selectedFile || !hasSelectedColumn) return
    }

    const storeItem = _mappingInfoToStoreItem(mappingInfo)
    const chrofyColumnCode = String(colMeta?.chrofyColumnCode ?? '')
    const previewBody = buildIf02099MappingPreviewBody({
      dataCreateTaskKey: urlParams.dataCreateTaskKey,
      dataKindCode: urlParams.genericKindCode,
      chrofyColumnCode,
      chrofyColumnName: colName,
      storeItem,
      limit: 5
    })

    dispatch({ type: 'SET_PREVIEW_LOADING', payload: true })
    try {
      const res = await getMappingPreview(globalStore, previewBody)
      const rows = mappingPreviewApiResponseToRows(res)
      const previewErrors = normalizeIf02099PreviewErrors(res)
      const previewWarnings = normalizeIf02099PreviewWarnings(res)
      dispatch({ type: 'SET_PREVIEW', payload: { rows, previewErrors, previewWarnings, previewApiError: null } })
    } catch (error) {
      dispatch({
        type: 'SET_PREVIEW',
        payload: {
          rows: [],
          previewErrors: [],
          previewWarnings: [],
          previewApiError: resolveMappingPreviewApiErrorMessage(error)
        }
      })
    }
  }, [
    state.selectedType,
    state.formData,
    fileTypes,
    fieldKey,
    colName,
    meta,
    urlParams.dataCreateTaskKey,
    urlParams.genericKindCode,
    globalStore,
    dispatch
  ])

  useEffect(() => {
    previewRef.current = runPreview
  }, [runPreview])

  // フッターの「プレビュー表示」ボタンは共通 Modal 側で showModal 時に一度だけ生成され、本文の
  // 種別変更では再描画されない。固定値(constant)選択時のみボタンを非活性にするため、種別変更に
  // 追随してフッターボタンの disabled を更新する。これはフッター再描画のためのブリッジであり、
  // core.md §4 の「init / モーダルフォーム同期」に準ずる例外として許容する。
  useEffect(() => {
    const isPreviewDisabled = PREVIEW_DISABLED_TYPES.includes(state.selectedType)
    updateButtons?.((buttons) =>
      buttons.map((button) =>
        button.name === PREVIEW_BUTTON_NAME ? { ...button, disabled: isPreviewDisabled } : button
      )
    )
  }, [state.selectedType, updateButtons])

  const getPreview = () => getMappingPreview(globalStore)
  const desc = state.selectedType ? MAPPING_TYPE_DESCRIPTIONS[state.selectedType] : null
  const onTypeChange = (value) => dispatch({ type: 'SET_TYPE', payload: { type: value, fileTypes } })
  const onFormChange = (fd) => dispatch({ type: 'SET_FORM_DATA', payload: { formData: fd } })
  const onPreviewLoad = (rows, previewErrors = [], previewWarnings = []) =>
    dispatch({ type: 'SET_PREVIEW', payload: { rows, previewErrors, previewWarnings, previewApiError: null } })
  const setPreviewLoading = (v) => dispatch({ type: 'SET_PREVIEW_LOADING', payload: v })

  const previewValidationIssues = useMemo(
    () => mapIf02099PreviewIssuesToValidationIssues(state.previewErrors, colName),
    [state.previewErrors, colName]
  )
  const previewRowErrorSet = useMemo(
    () => getValidationIssueCellKeySet(previewValidationIssues),
    [previewValidationIssues]
  )
  const previewColumnLevelErrorSet = useMemo(
    () => getValidationIssueColumnLevelSet(previewValidationIssues),
    [previewValidationIssues]
  )
  const previewErrorRowNumberSet = useMemo(() => {
    const set = new Set()
    previewValidationIssues.forEach((issue) => {
      if (isPositiveRowNumber(issue.rowNumber)) {
        set.add(Number(issue.rowNumber))
      }
    })
    return set
  }, [previewValidationIssues])

  const previewWarningValidationIssues = useMemo(
    () => mapIf02099PreviewIssuesToValidationIssues(state.previewWarnings, colName),
    [state.previewWarnings, colName]
  )
  const previewRowWarningSet = useMemo(
    () => getValidationIssueCellKeySet(previewWarningValidationIssues),
    [previewWarningValidationIssues]
  )
  const previewColumnLevelWarningSet = useMemo(
    () => getValidationIssueColumnLevelSet(previewWarningValidationIssues),
    [previewWarningValidationIssues]
  )
  const previewWarningRowNumberSet = useMemo(() => {
    const set = new Set()
    previewWarningValidationIssues.forEach((issue) => {
      if (isPositiveRowNumber(issue.rowNumber)) {
        set.add(Number(issue.rowNumber))
      }
    })
    return set
  }, [previewWarningValidationIssues])

  const previewTargetColumnLevelError = previewColumnLevelErrorSet.has(colName)
  const previewTargetColumnLevelWarning = previewColumnLevelWarningSet.has(colName)

  return (
    <ChrofyCustomSelectContext.Provider value={MAPPING_MODAL_SELECT_CONTEXT}>
      <div id="mapping-config-modal-content" className="text-base space-y-4">
        {/* 種別選択 */}
        <div>
          <label className="block font-semibold mb-2 text-[#374151]">マッピング種別を選択</label>
          <select
            id="modal_mapping_type"
            className="w-full border border-[#D0D7E5] rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#3150A5]"
            value={state.selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
          >
            <option value="">-- 種別を選択してください --</option>
            {MAPPING_TYPE_GROUPS.map((group) => {
              const visibleOptions = group.options.filter((opt) => !(opt.value === 'four_arithmetic' && !isNumeric))
              if (visibleOptions.length === 0) return null
              return (
                <optgroup key={group.label} label={group.label}>
                  {visibleOptions.map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={opt.value === 'value_mapping_dnd' && systemValues.length === 0}
                    >
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              )
            })}
          </select>
          {state.errors?.selectedType && <ChrofyShowErrors field="selectedType" errors={state.errors} />}
        </div>

        {/* 種別説明 */}
        {desc && (
          <div className="p-4 rounded-lg border border-[#bae6fd] bg-[#f0f9ff]">
            <div className="font-semibold text-[#0369a1] mb-1">💡 {desc.title}</div>
            <div className="text-sm text-[#475569] mb-2">{desc.desc}</div>
            <div className="text-xs text-[#64748b] bg-white p-2 rounded border-l-4 border-[#0ea5e9]">
              {desc.example}
            </div>
          </div>
        )}

        {/* simple: インラインフォーム（プレビューはフッターボタンから） */}
        {state.selectedType === 'simple' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <SimpleMappingForm
              fileTypes={fileTypes}
              initialMappingInfo={null}
              formData={{
                mappingFileType: (state.formData.mappingFileType ?? '').toString(),
                mappingColumnName: (state.formData.mappingColumnName ?? '').toString()
              }}
              errors={state.errors}
              onChange={(fd) => onFormChange(fd)}
            />
          </div>
        )}

        {/* constant: インラインフォーム */}
        {state.selectedType === 'constant' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <ConstantForm value={state.formData} errors={state.errors} onChange={onFormChange} />
          </div>
        )}

        {/* date_format: インラインフォーム */}
        {state.selectedType === 'date_format' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <DateFormatForm
              fileTypes={fileTypes}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
              getPreview={getPreview}
              urlParams={urlParams}
              previewRows={state.previewRows}
              onPreviewLoad={onPreviewLoad}
              previewLoading={state.previewLoading}
              setPreviewLoading={setPreviewLoading}
            />
          </div>
        )}

        {/* time_format: インラインフォーム */}
        {state.selectedType === 'time_format' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <TimeFormatForm
              fileTypes={fileTypes}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
            />
          </div>
        )}

        {/* conversion_table: インラインフォーム */}
        {state.selectedType === 'conversion_table' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <ConversionTableForm
              fileTypes={fileTypes}
              conversionTableList={conversionTableList}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
              getPreview={getPreview}
              urlParams={urlParams}
              previewRows={state.previewRows}
              onPreviewLoad={onPreviewLoad}
              previewLoading={state.previewLoading}
              setPreviewLoading={setPreviewLoading}
            />
          </div>
        )}

        {/* value_mapping: インラインフォーム */}
        {state.selectedType === 'value_mapping' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <ValueMappingInputForm
              fileTypes={fileTypes}
              urlParams={urlParams}
              globalStore={globalStore}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
              getPreview={getPreview}
              previewRows={state.previewRows}
              onPreviewLoad={onPreviewLoad}
              previewLoading={state.previewLoading}
              setPreviewLoading={setPreviewLoading}
            />
          </div>
        )}

        {/* value_mapping_dnd: インラインフォーム */}
        {state.selectedType === 'value_mapping_dnd' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <ValueMappingDndForm
              fileTypes={fileTypes}
              systemValues={systemValues}
              onChange={onFormChange}
              formData={state.formData}
              errors={state.errors}
              getPreview={getPreview}
              previewRows={state.previewRows}
              onPreviewLoad={onPreviewLoad}
              previewLoading={state.previewLoading}
              setPreviewLoading={setPreviewLoading}
            />
          </div>
        )}

        {/* four_arithmetic: インラインフォーム */}
        {state.selectedType === 'four_arithmetic' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <FourArithmeticForm
              fileTypes={fileTypes}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
              getPreview={getPreview}
              urlParams={urlParams}
              previewRows={state.previewRows}
              onPreviewLoad={onPreviewLoad}
              previewLoading={state.previewLoading}
              setPreviewLoading={setPreviewLoading}
            />
          </div>
        )}

        {/* single_condition: インラインフォーム */}
        {state.selectedType === 'single_condition' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <SingleConditionForm
              fileTypes={fileTypes}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
            />
          </div>
        )}

        {/* multi_condition: インラインフォーム */}
        {state.selectedType === 'multi_condition' && (
          <div className="p-4 rounded-lg border border-[#E0E5F2] bg-[#f8fafc] border-l-4 border-l-[#3150A5]">
            <MultiConditionForm
              fileTypes={fileTypes}
              formData={state.formData}
              errors={state.errors}
              onChange={onFormChange}
            />
          </div>
        )}

        {/* プレビュー表示セクション（フッターの「プレビュー表示」ボタン押下後のみ表示） */}
        {state.previewRequested && (
          <section className="rounded-lg border border-[#E0E5F2] bg-white p-4">
            <h3 className="font-semibold text-[#374151] mb-3 flex items-center gap-2">
              <span className="inline-block w-5 h-5 rounded bg-[#e0e7ff] flex items-center justify-center text-[#4338ca] text-xs">
                📊
              </span>
              プレビュー
            </h3>
            {state.previewLoading ? (
              <div className="rounded border border-[#dbeafe] bg-[#f8fbff] px-4 py-5">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#e0e7ff] text-[#4338ca] animate-pulse">
                    ⏳
                  </span>
                  <div>
                    <p className="font-medium text-[#334155]">プレビューを取得しています...</p>
                    <p className="text-sm text-[#64748b]">現在のマッピング設定を反映した結果を読み込み中です。</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xs text-[#64748b] mb-3 flex items-start gap-2">
                  <span className="text-amber-500 shrink-0">⚠</span>
                  設定したマッピングルールを実際のデータに適用した結果を表示します（最初の5行）
                </p>
                {state.previewRows?.length > 0 ? (
                  <>
                    <div className="overflow-x-auto rounded border border-[#e2e8f0]">
                      <table className="base-table w-full text-sm">
                        <thead>
                          <tr>
                            {Object.keys(state.previewRows[0] || {}).map((k) => (
                              <th key={k} className="base-table-header">
                                {k}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {state.previewRows.slice(0, 5).map((row, i) => {
                            const rowNumber = i + 1
                            const isErrorRow = previewErrorRowNumberSet.has(rowNumber)
                            const isWarningRow = !isErrorRow && previewWarningRowNumberSet.has(rowNumber)
                            const rowClassName = isErrorRow
                              ? 'bg-red-50 text-red-900'
                              : isWarningRow
                                ? 'bg-amber-50 text-amber-900'
                                : undefined
                            return (
                              <tr key={i} className={rowClassName}>
                                {Object.keys(state.previewRows[0] || {}).map((colKey) => {
                                  const cellKey = `${rowNumber}-${colKey}`
                                  const cellValue = row[colKey]
                                  const isMappedResultColumn = colKey === colName
                                  const isErrorCell =
                                    previewRowErrorSet.has(cellKey) || previewColumnLevelErrorSet.has(colKey)
                                  const isWarningCell =
                                    previewRowWarningSet.has(cellKey) || previewColumnLevelWarningSet.has(colKey)
                                  const showError = isErrorCell || isErrorRow || previewTargetColumnLevelError
                                  const showWarning =
                                    !showError && (isWarningCell || isWarningRow || previewTargetColumnLevelWarning)
                                  const showErrorIcon = showError && !isMappedResultColumn
                                  const showWarningIcon = showWarning && !isMappedResultColumn
                                  const isEmpty = cellValue === undefined || cellValue === null || cellValue === ''
                                  const cellClassName = showError
                                    ? 'cursor-pointer bg-red-50 text-red-900'
                                    : showWarning
                                      ? 'bg-amber-50 text-amber-900'
                                      : undefined
                                  return (
                                    <td
                                      key={colKey}
                                      className={cellClassName}
                                      title={getValidationMappingCellTitle({
                                        isError: showError,
                                        isWarning: showWarning,
                                        isEmpty,
                                        rawValue: cellValue
                                      })}
                                      data-testid={
                                        showErrorIcon
                                          ? 'mapping-preview-error-cell'
                                          : showWarningIcon
                                            ? 'mapping-preview-warning-cell'
                                            : undefined
                                      }
                                    >
                                      {renderPreviewCell(cellValue, { showErrorIcon, showWarningIcon })}
                                    </td>
                                  )
                                })}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                    <ValidationMappingLegend />
                  </>
                ) : (
                  <div className="py-2">
                    {state.previewApiError ? (
                      <p className="text-sm text-red-500" data-testid="mapping-preview-api-error">
                        {state.previewApiError}
                      </p>
                    ) : (
                      <p className="text-sm text-[#64748b]">該当データがありません。</p>
                    )}
                  </div>
                )}
                {Array.isArray(state.previewErrors) && state.previewErrors.length > 0 && (
                  <div
                    className="mt-4 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-3"
                    data-testid="mapping-preview-if02099-errors"
                  >
                    <p className="text-xs font-semibold text-amber-900 mb-2">マッピング検証エラー</p>
                    <ul className="space-y-2 text-sm text-[#78350f] list-none m-0 p-0">
                      {state.previewErrors.map((err, idx) => {
                        const head = formatIf02099PreviewIssueHead(err.row, err.column, colName)
                        return (
                          <li key={idx} className="border-l-4 border-amber-400 pl-2">
                            {head ? <span className="font-medium text-amber-950">{head}</span> : null}
                            {head && err.message ? <span className="text-amber-800"> — </span> : null}
                            {err.message ? <span>{err.message}</span> : null}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
                {Array.isArray(state.previewWarnings) && state.previewWarnings.length > 0 && (
                  <div
                    className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50/90 px-3 py-3"
                    data-testid="mapping-preview-if02099-warnings"
                  >
                    <p className="text-xs font-semibold text-yellow-900 mb-2">マッピング検証警告</p>
                    <ul className="space-y-2 text-sm text-[#854d0e] list-none m-0 p-0">
                      {state.previewWarnings.map((warn, idx) => {
                        const head = formatIf02099PreviewIssueHead(warn.row, warn.column, colName)
                        return (
                          <li key={idx} className="border-l-4 border-yellow-400 pl-2">
                            {head ? <span className="font-medium text-yellow-950">{head}</span> : null}
                            {head && warn.message ? <span className="text-yellow-800"> — </span> : null}
                            {warn.message ? <span>{warn.message}</span> : null}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </ChrofyCustomSelectContext.Provider>
  )
}

/**
 * マッピング設定モーダルを開くフック。Context でモーダル内 state を管理。
 *
 * @param {{
 *   urlParams: { dataCreateTaskKey: string; genericKindCode: string }
 *   globalStore: import('redux').Store
 * }} options
 */
export function useMappingConfigModal(options) {
  const { urlParams, globalStore, fileTypes, meta, onAddMappingItem } = options || {}
  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()
  const { promisify } = useModifyClassList('.modal-window', ['w-[1400px]', 'max-w-[95svw]'])
  const applyRef = useRef(null)
  const previewRef = useRef(null)

  const openMappingConfigModal = ({
    fieldKey,
    colName,
    initialMappingInfo = null,
    mode = 'add',
    onSubmitMappingItem = onAddMappingItem
  }) => {
    return promisify((resolve) => {
      showModal({
        title: `${colName} のマッピング設定 - ${mode === 'edit' ? '編集' : '追加'}`,
        bodyClassName: 'pb-6',
        body: (
          <MappingModalProvider>
            <MappingConfigModalContent
              urlParams={urlParams}
              globalStore={globalStore}
              fieldKey={fieldKey}
              colName={colName}
              initialMappingInfo={initialMappingInfo}
              applyRef={applyRef}
              previewRef={previewRef}
              fileTypes={fileTypes}
              meta={meta}
              onSubmitMappingItem={onSubmitMappingItem}
            />
          </MappingModalProvider>
        ),
        button: [
          {
            name: PREVIEW_BUTTON_NAME,
            // 固定値(constant)選択時は disabled になる。button_back には :disabled のスタイルが
            // ないため、Tailwind の disabled ユーティリティでグレーアウト表示して非活性を可視化する。
            className: 'footer-btn button_back disabled:opacity-50 disabled:cursor-not-allowed',
            click: () => previewRef.current?.(),
            close: false
          },
          {
            name: '設定を適用',
            className: 'footer-btn button_base',
            click: () => {
              const hasErrors = applyRef.current?.()
              if (!hasErrors) {
                _closeUpModal()
                return
              }
              // 設計書 §9.1: エラー時は最初のエラー項目（エラーメッセージ）までスクロールする
              requestAnimationFrame(() => {
                document
                  .getElementById('mapping-config-modal-content')
                  ?.querySelector('.text-red-500')
                  ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
              })
            },
            close: false
          }
        ],
        isCancel: true,
        isWide: true,
        cancelName: 'キャンセル',
        onClose: resolve
      })
    })
  }

  return { openMappingConfigModal }
}

export default MappingConfigModalContent
